import { Locales } from '@griply/types';
import { useMemo } from 'react';
import { NLP } from 'shared/constants';
import { Person } from 'shared/types/nlp';
import { getAllDatePatterns } from 'shared/utils/nlp/date-processing';
import { getTargetPatternContent } from 'shared/utils/nlp/frequency-processing';
import { getAtKeyword } from 'shared/utils/nlp/locale-vocabulary';
import { getReminderPatternString } from 'shared/utils/nlp/reminder-processing';
import { getRrulePatternContent } from 'shared/utils/nlp/rrule-processing';

export type Options = {
  language: Locales;
  people?: Person[];
};

export const useNlpRegex = ({ language, people }: Options) =>
  useMemo(() => {
    const allDatePatterns = getAllDatePatterns(language);
    const durationPattern = `\\b${NLP.durationPattern}\\b`;
    const targetPatternContent = getTargetPatternContent(language);
    const rrulePatternContent = getRrulePatternContent(language);

    const atTimePatternString = `\\b${getAtKeyword(language)}\\s+(${NLP.timePattern})\\b`;

    // This regex safely replaces capturing group parentheses `(` with non-capturing ones `(?:`.
    // It avoids replacing parentheses that are part of special groups like lookaheads `(?=`, `(?!`.
    const safeReplaceRegex = /\((?!\?)/g;

    // A flexible pattern for goals, tags, and people that allows for special characters.
    const goalPattern = `~${NLP.goalSubPattern}`;
    const tagPattern = `#${NLP.tagSubPattern}`;
    const personPattern = `(?<!~)@${NLP.personSubPattern}`;

    const parts = [
      `(?<linkedItem>${goalPattern})`, // Group 1: Goals
      `(?<tag>${tagPattern})`, // Group 2: Tags
      people?.length ? `(?<person>${personPattern})` : '', // Group 3: People
      `(?<priority>\\bp[1-3]\\b|!![1-3])`, // Group 4: Priority (e.g., p1, !!1)
      `(?<deadline>\\{${allDatePatterns}\\})`, // Group 5: Deadlines (e.g., {today})
      `(?<target>\\b${targetPatternContent.replace(safeReplaceRegex, '(?:')}\\b)`, // Group 6: Targets
      `(?<rrule>${rrulePatternContent.replace(safeReplaceRegex, '(?:')})`, // Group 7: Recurrence rules
      `(?<dueDate>(?<![a-zA-Z0-9~#@!{])(?:${allDatePatterns})(?![a-zA-Z0-9}]))`, // Group 8: Due Dates
      `(?<timeRange>${NLP.timeRangePatternString})`, // Group 9: Time ranges (has 2 inner groups -> 10, 11)
      `(?<singleTime>${NLP.singleTimePatternString})`, // Group 12: Single times (has 1 inner group -> 13)
      `(?<singleTimeAt>${atTimePatternString})`, // Group 14 (inner 15): (at 9am)
      `(?<duration>${durationPattern})`, // Group 16: Durations
      `(?<reminder>${getReminderPatternString(language)})`, // Group 17: Reminders
    ].filter(Boolean);
    return new RegExp(parts.join('|'), 'giu');
  }, [language, people?.length]);
